import React, {useEffect, useMemo, useState} from 'react';
import {useLazyQuery, useQuery} from '@apollo/client';
import {
    Button,
    Card,
    Col,
    Descriptions,
    Drawer,
    Image,
    Input,
    Row,
    Skeleton,
    Space,
    Timeline,
    Typography,
    Upload,
    message,
} from 'antd';
import type {RcFile, UploadFile} from 'antd/es/upload/interface';
import {PlusOutlined, UploadOutlined} from '@ant-design/icons';
import {useTranslation} from 'react-i18next';
import {formatEpochToDateTime} from '../../../../utils/dateTimeUtils';
import {normalizeLang, pickDisplayText, renderReviewComment} from '../../../../utils/i18nText';
import {validateAppealFile} from '../../../../utils/appealAttachment';
import {fileUpload} from '../../../../utils/storageUtils';
import {showError} from '../../../../utils/exceptionUtils';
import AttachmentTile from '../../../../components/AttachmentTile';
import RichTextView from '../../../../components/RichTextView';
import {resolveComplaintSourceFilter} from '../utils/complaintSource';
import AbandonConfirmModal from './AbandonConfirmModal';
import {
    GET_COMPLAINT_APPEAL_DETAIL,
    GetComplaintAppealDetailResponse,
    GetComplaintAppealDetailVariables,
} from '../graphql/queries/getComplaintAppealDetail';
import {
    GET_CUSTOMER_COMPLAINT_BY_ID,
    GetCustomerComplaintByIdResponse,
    GetCustomerComplaintByIdVariables,
} from '../graphql/queries/getCustomerComplaintById';
import {
    ComplaintAppealDetail,
    ComplaintStatus,
    CustomerComplaint,
} from '../graphql/types/CustomerComplaintType';
import {
    useAbandonCustomerComplaintAppeal,
    useAcknowledgeCustomerComplaint,
    useSubmitCustomerComplaintAppeal,
} from '../hooks/useCustomerComplaintActions';

const {Text} = Typography;
const {TextArea} = Input;

const APPEAL_REASON_MIN = 1;
const APPEAL_REASON_MAX = 500;
const MAX_IMAGE_ATTACHMENTS = 3;

type AttachmentStatus = 'uploading' | 'done' | 'error';

/**
 * 客诉详情展示（对齐审核侧 ReviewTaskDetailDrawer.ComplaintReasonView）：
 * 默认按当前 locale 显示译文，存在真实译文时提供「显示原文/译文」切换；
 * 文本可能含外部富文本，统一经 RichTextView（DOMPurify 净化）渲染，防存储型 XSS。
 */
const ComplaintReasonView: React.FC<{
    reason: {text: string; lang: string; translations: Array<{lang: string; text: string}>};
    currentLocale: string;
    showOriginalLabel: string;
    showTranslationLabel: string;
}> = ({reason, currentLocale, showOriginalLabel, showTranslationLabel}) => {
    const [showOriginal, setShowOriginal] = useState(false);
    const translated = pickDisplayText(reason, currentLocale);
    const original = reason.text || '';
    // 命中当前 locale 的 translations 才算真译文（与 pickDisplayText 同口径）；否则是原文兜底，切换无意义
    const hasTranslation = !!reason.translations?.find((e) => e.lang === currentLocale)?.text;
    const hasDistinctOriginal = hasTranslation && !!original;
    const displayText = showOriginal ? original : translated;

    return (
        <div>
            {hasDistinctOriginal && (
                <div className="flex justify-end">
                    <Button
                        type="link"
                        size="small"
                        className="px-0"
                        onClick={() => setShowOriginal((v) => !v)}
                    >
                        {showOriginal ? showTranslationLabel : showOriginalLabel}
                    </Button>
                </div>
            )}
            <RichTextView
                html={displayText}
                className="text-sm mt-1 p-3 [&_img]:max-w-full"
                style={{background: '#fafafa', borderRadius: 4, whiteSpace: 'pre-wrap'}}
            />
        </div>
    );
};

/**
 * 申诉凭证（选时预上传）：选中即异步上传到 S3，提交时直接取 objectKey。
 * status=uploading 时禁用提交，error 项可点击重传。
 */
interface AppealAttachment {
    uid: string;
    file: RcFile;
    objectKey?: string;
    status: AttachmentStatus;
}

interface CustomerComplaintDetailDrawerProps {
    open: boolean;
    /** 进入详情时传入客诉 ID；Drawer 内部主动拉详情，保证看到的是最新数据 */
    complaintId?: number;
    /**
     * 是否仅查看视角（wms / HQ）。
     * true 时隐藏申诉表单、认责 / 提交申诉 / 放弃 按钮，仅展示详情。
     */
    viewOnly?: boolean;
    onClose: () => void;
    onChanged: () => void;
}

const formatTime = formatEpochToDateTime;

interface TimelineItem {
    dot?: React.ReactNode;
    color?: string;
    label: string;
    title: string;
    description?: string;
}

/**
 * 客诉下发节点的操作人 i18n key：复用聚合规则
 *   - 客服（CUSTOMER_SERVICE / EMAIL/PHONE）→ customerService
 *   - 系统（SYSTEM / OTHER / 未识别）       → system
 */
const resolveComplaintSourceOperatorKey = (source?: string): string => {
    const filter = resolveComplaintSourceFilter(source);
    return filter === 'CUSTOMER_SERVICE'
        ? 'customerComplaint.timeline.operator.customerService'
        : 'customerComplaint.timeline.operator.system';
};

/** 根据客诉及申诉信息拼装问题件记录时间轴（最新在顶） */
const buildTimelineItems = (
    record: CustomerComplaint,
    appealDetail: ComplaintAppealDetail | undefined,
    t: (key: string) => string,
): TimelineItem[] => {
    const items: TimelineItem[] = [];

    // 1. 上报问题件（创建）；下发人按 source 区分客服 vs 系统
    if (record.createdAt) {
        const operatorLabel = t(resolveComplaintSourceOperatorKey(record.source));
        items.push({
            color: 'green',
            label: formatTime(record.createdAt),
            title: t('customerComplaint.timeline.created'),
            description: `${t('customerComplaint.timeline.operatorLabel')}：${operatorLabel}`,
        });
    }

    // 2. DSP 提交申诉（不带申诉理由描述，申诉详情区已展示）
    if (appealDetail?.appealSubmittedAt) {
        items.push({
            color: 'blue',
            label: formatTime(appealDetail.appealSubmittedAt),
            title: t('customerComplaint.timeline.appealSubmitted'),
        });
    }

    // 3. 申诉复审完成
    if (appealDetail?.reviewedAt && appealDetail.reviewResult) {
        const resultLabel = t(`customerComplaint.reviewResult.${appealDetail.reviewResult}`);
        items.push({
            color: appealDetail.reviewResult === 'APPROVED' ? 'green' : 'red',
            label: formatTime(appealDetail.reviewedAt),
            title: t('customerComplaint.timeline.appealReviewed'),
            description: resultLabel,
        });
    }

    // 4. DSP 自主认责 / 自动超时（终态但无审核结果）
    if (record.isFinished === 1 && record.finishedAt && !appealDetail?.reviewedAt) {
        const isTimeout = record.status === ComplaintStatus.TIMEOUT;
        // 操作人：主动认责=认责 DSP（operatorName），超时认责=系统（operatorName 为空兜底）
        const operatorLabel = record.operatorName || t('customerComplaint.timeline.operator.system');
        items.push({
            color: isTimeout ? 'red' : 'gray',
            label: formatTime(record.finishedAt),
            title: isTimeout
                ? t('customerComplaint.timeline.timeout')
                : t('customerComplaint.timeline.dspAcknowledged'),
            description: `${t('customerComplaint.timeline.operatorLabel')}：${operatorLabel}`,
        });
    }

    // 最新在顶（与 mobile 一致）
    return items.reverse();
};

const CustomerComplaintDetailDrawer: React.FC<CustomerComplaintDetailDrawerProps> = ({
    open,
    complaintId,
    viewOnly = false,
    onClose,
    onChanged,
}) => {
    const {t, i18n} = useTranslation();
    const [appealReason, setAppealReason] = useState('');
    const [submitting, setSubmitting] = useState(false);
    // PENDING 凭证上传：图片 / 其他文件（按 mime 分组渲染，提交时合并到同一 appealAttachmentS3KeysJson 数组）。
    // 选时即异步上传到 S3，元素带 status/objectKey，提交时直接取 objectKey 不再上传。
    const [imageFiles, setImageFiles] = useState<AppealAttachment[]>([]);
    const [otherFiles, setOtherFiles] = useState<AppealAttachment[]>([]);
    // 图片预览 blob URL 按 imageFiles 派生（而非每次 render 新建），并在变化/卸载时回收，避免内存泄漏
    const imagePreviewUrls = useMemo(
        () => new Map(imageFiles.map((item) => [item.uid, URL.createObjectURL(item.file)])),
        [imageFiles],
    );
    useEffect(() => {
        return () => {
            imagePreviewUrls.forEach((url) => URL.revokeObjectURL(url));
        };
    }, [imagePreviewUrls]);

    // 放弃申诉确认弹窗：详情抽屉同时承担 acknowledge / abandon 两种动作，统一走共用 Modal
    const [abandonAction, setAbandonAction] = useState<'acknowledge' | 'abandon' | null>(null);
    const [abandonLoading, setAbandonLoading] = useState(false);

    const {data: recordResp, loading: recordLoading} = useQuery<
        GetCustomerComplaintByIdResponse,
        GetCustomerComplaintByIdVariables
    >(GET_CUSTOMER_COMPLAINT_BY_ID, {
        variables: {id: complaintId ?? 0},
        skip: !open || complaintId == null,
        fetchPolicy: 'no-cache',
    });

    const [fetchAppealDetail, {data: appealResp, loading: appealLoading}] = useLazyQuery<
        GetComplaintAppealDetailResponse,
        GetComplaintAppealDetailVariables
    >(GET_COMPLAINT_APPEAL_DETAIL, {fetchPolicy: 'no-cache'});

    const [submitAppeal] = useSubmitCustomerComplaintAppeal();
    const [acknowledge] = useAcknowledgeCustomerComplaint();
    const [abandonAppeal] = useAbandonCustomerComplaintAppeal();

    // 切换工单时 recordResp 在新查询返回前仍是上一条；仅当返回的 id 与当前 complaintId 匹配才采用
    const rawRecord = recordResp?.getCustomerComplaintById;
    const record: CustomerComplaint | undefined =
        rawRecord && rawRecord.id === complaintId ? rawRecord : undefined;

    useEffect(() => {
        if (!open || !record) return;
        setAppealReason('');
        setImageFiles([]);
        setOtherFiles([]);
        if (record.status !== ComplaintStatus.PENDING_DSP_ACTION) {
            fetchAppealDetail({variables: {complaintId: record.id}});
        }
    }, [open, record, fetchAppealDetail]);

    if (!open || complaintId == null) return null;
    if (recordLoading || !record) {
        return (
            <Drawer open={open} onClose={onClose} width={1080} destroyOnClose>
                <Skeleton active />
            </Drawer>
        );
    }

    const isPending = record.status === ComplaintStatus.PENDING_DSP_ACTION;
    // useLazyQuery 的 appealResp 会残留上一条已查工单的数据：
    //   - 未申诉（PENDING）不查 appealDetail，强制置空
    //   - 切换工单时仅当返回的 complaintId 与当前 record 匹配才采用，避免渲染上一条的残留
    const rawAppealDetail = appealResp?.getCustomerComplaintAppealDetailByComplaintId;
    const appealDetail: ComplaintAppealDetail | undefined =
        !isPending && rawAppealDetail?.complaintId === record.id ? rawAppealDetail : undefined;
    const attachments = appealDetail?.appealAttachmentS3UrlsJson ?? [];

    const isAppealing = record.status === ComplaintStatus.APPEALING;
    // 仅查看视角：所有操作按钮 / 申诉表单都隐藏，只读
    const canOperate = !viewOnly;
    // 前端兜底：已过申诉截止但定时任务尚未把 status 流转为 TIMEOUT 时，禁止提交（后端仍会校验，此处避免无效请求 + 裸 key）
    const isAppealExpired =
        record.appealDeadlineAt != null && record.appealDeadlineAt * 1000 < Date.now();

    const timelineItems = buildTimelineItems(record, appealDetail, t);

    // 校验申诉凭证：视频需读元数据（异步），期间展示 loading 提示避免用户以为无响应；
    // 图片 / 其它文件为同步校验，不显示 loading
    const validateAppealFileWithFeedback = async (file: File) => {
        const isVideo = file.type.startsWith('video/');
        const loadingKey = 'appeal-file-validating';
        if (isVideo) {
            message.loading({
                key: loadingKey,
                content: t('customerComplaint.drawer.validatingFile') as string,
                duration: 0,
            });
        }
        try {
            return await validateAppealFile(file);
        } finally {
            if (isVideo) message.destroy(loadingKey);
        }
    };

    // 选时预上传：校验通过后置入 uploading 占位，异步上传 S3，成功写 objectKey/done，失败置 error（可重传）。
    const startUpload = (
        file: RcFile,
        setFiles: React.Dispatch<React.SetStateAction<AppealAttachment[]>>,
    ) => {
        const tag = record.externalRefId ?? '';
        setFiles((prev) => [...prev, {uid: file.uid, file, status: 'uploading'}]);
        fileUpload(file, tag)
            .then(({objectKey}) => {
                setFiles((prev) =>
                    prev.map((item) =>
                        item.uid === file.uid ? {...item, objectKey, status: 'done'} : item,
                    ),
                );
            })
            .catch(() => {
                setFiles((prev) =>
                    prev.map((item) =>
                        item.uid === file.uid ? {...item, status: 'error'} : item,
                    ),
                );
            });
    };

    // 失败项重传：保留 uid，重置为 uploading 后再次上传
    const retryUpload = (
        attachment: AppealAttachment,
        setFiles: React.Dispatch<React.SetStateAction<AppealAttachment[]>>,
    ) => {
        const tag = record.externalRefId ?? '';
        setFiles((prev) =>
            prev.map((item) =>
                item.uid === attachment.uid ? {...item, status: 'uploading'} : item,
            ),
        );
        fileUpload(attachment.file, tag)
            .then(({objectKey}) => {
                setFiles((prev) =>
                    prev.map((item) =>
                        item.uid === attachment.uid ? {...item, objectKey, status: 'done'} : item,
                    ),
                );
            })
            .catch(() => {
                setFiles((prev) =>
                    prev.map((item) =>
                        item.uid === attachment.uid ? {...item, status: 'error'} : item,
                    ),
                );
            });
    };

    const allAttachments = [...imageFiles, ...otherFiles];
    const hasUploading = allAttachments.some((item) => item.status === 'uploading');
    const hasUploadError = allAttachments.some((item) => item.status === 'error');

    const handleSubmitAppeal = async () => {
        // 已过申诉截止：前端兜底拦截（按钮已 disabled，此处防御）
        if (isAppealExpired) {
            message.warning(t('customerComplaint.drawer.appealExpired') as string);
            return;
        }
        // 备注必填校验
        const trimmedReason = appealReason.trim();
        if (trimmedReason.length < APPEAL_REASON_MIN) {
            message.warning(t('customerComplaint.drawer.remarkRequired') as string);
            return;
        }
        if (trimmedReason.length > APPEAL_REASON_MAX) {
            message.warning(
                t('customerComplaint.drawer.appealReasonLength', {
                    min: APPEAL_REASON_MIN,
                    max: APPEAL_REASON_MAX,
                })
            );
            return;
        }
        // 凭证至少一项
        if (allAttachments.length === 0) {
            message.warning(t('customerComplaint.drawer.attachmentRequired') as string);
            return;
        }
        // 上传中 / 存在失败项时不允许提交（按钮已 disabled，此处兜底）
        if (hasUploading) {
            message.warning(t('customerComplaint.drawer.attachmentUploading') as string);
            return;
        }
        if (hasUploadError) {
            message.warning(t('customerComplaint.drawer.attachmentUploadFailed') as string);
            return;
        }
        try {
            setSubmitting(true);
            // 图片在前、其他附件在后；预上传已拿到 objectKey，提交直接收集
            const s3Keys = allAttachments.map((item) => item.objectKey!);
            await submitAppeal({
                variables: {
                    complaintId: record.id,
                    appealReason: trimmedReason,
                    appealAttachmentS3KeysJson: JSON.stringify(s3Keys),
                },
            });
            message.success(t('customerComplaint.drawer.submitSuccess'));
            onChanged();
            onClose();
        } catch (e: any) {
            // 解析后端 BusinessException 的 messageKey 走 i18n（如申诉超时 appealSlaExpired），避免裸 key
            showError(e);
        } finally {
            setSubmitting(false);
        }
    };

    const handleAcknowledge = () => setAbandonAction('acknowledge');
    const handleAbandon = () => setAbandonAction('abandon');

    const handleAbandonCancel = () => {
        if (abandonLoading) return;
        setAbandonAction(null);
    };

    const handleAbandonConfirm = async () => {
        if (!abandonAction) return;
        setAbandonLoading(true);
        try {
            if (abandonAction === 'acknowledge') {
                await acknowledge({variables: {complaintId: record.id}});
                message.success(t('customerComplaint.drawer.acknowledgeSuccess'));
            } else {
                await abandonAppeal({variables: {complaintId: record.id}});
                message.success(t('customerComplaint.drawer.abandonSuccess'));
            }
            setAbandonAction(null);
            onChanged();
            onClose();
        } catch (e: any) {
            // 解析后端 BusinessException 的 messageKey 走 i18n，避免裸 key
            showError(e);
        } finally {
            setAbandonLoading(false);
        }
    };

    return (
        <>
        <Drawer
            open={open}
            onClose={onClose}
            title={
                <div className="flex items-center" style={{paddingLeft: 4}}>
                    <span
                        style={{
                            display: 'inline-block',
                            width: 4,
                            height: 18,
                            background: '#722ed1',
                            marginRight: 12,
                            borderRadius: 2,
                        }}
                    />
                    <span style={{fontSize: 16, fontWeight: 600}}>
                        {record.trackingNumber
                            ? t('customerComplaint.drawer.titleWithTracking', {trackingNumber: record.trackingNumber})
                            : t('customerComplaint.drawer.title', {id: record.id})}
                    </span>
                </div>
            }
            width={1080}
            destroyOnClose
            styles={{
                body: {background: '#f5f5f7', padding: 24},
                footer: {background: '#f5f5f7', borderTop: 'none'},
            }}
            footer={
                <div className="flex justify-center items-center gap-4 py-2">
                    {/* PENDING：放弃 + 申诉（"放弃" 实际调 acknowledge 接口，与列表行为一致） */}
                    {canOperate && isPending && (
                        <>
                            <Button
                                size="large"
                                onClick={handleAcknowledge}
                                style={{minWidth: 160, borderRadius: 999}}
                            >
                                {t('customerComplaint.drawer.abandon')}
                            </Button>
                            <Button
                                type="primary"
                                size="large"
                                loading={submitting}
                                disabled={hasUploading || isAppealExpired}
                                onClick={handleSubmitAppeal}
                                style={{minWidth: 160, borderRadius: 999}}
                            >
                                {t('customerComplaint.drawer.submitAppeal')}
                            </Button>
                        </>
                    )}
                    {/* APPEALING：放弃（调 abandon 接口，含级联 cancel review_task） */}
                    {canOperate && isAppealing && (
                        <Button
                            danger
                            size="large"
                            onClick={handleAbandon}
                            style={{minWidth: 160, borderRadius: 999}}
                        >
                            {t('customerComplaint.drawer.abandon')}
                        </Button>
                    )}
                    {/* 仅查看视角 / 终态：底部只显示关闭 */}
                    {(!canOperate || (!isPending && !isAppealing)) && (
                        <Button
                            size="large"
                            onClick={onClose}
                            style={{minWidth: 160, borderRadius: 999}}
                        >
                            {t('common.close')}
                        </Button>
                    )}
                </div>
            }
        >
            <Row gutter={16} style={{minHeight: '100%'}} align="stretch">
                {/* 左侧：基础信息 + 申诉表单/详情 */}
                <Col span={14} style={{display: 'flex', flexDirection: 'column', gap: 16}}>
                    <Card
                        title={
                            <span style={{fontSize: 15, fontWeight: 600}}>
                                {t('customerComplaint.drawer.packageInfo')}
                            </span>
                        }
                        bordered={false}
                        style={{borderRadius: 12}}
                    >
                        <Descriptions
                            column={1}
                            colon={false}
                            labelStyle={{color: '#8c8c8c', width: 96}}
                            contentStyle={{color: '#262626'}}
                        >
                            <Descriptions.Item label={t('customerComplaint.table.type')}>
                                {t(
                                    `customerComplaint.type.${record.complaintType}`,
                                    record.complaintType,
                                )}
                            </Descriptions.Item>
                            {record.complaintReason && (record.complaintReason.text || '').length > 0 && (
                                <Descriptions.Item label={t('customerComplaint.detail.complaintDetail')}>
                                    <ComplaintReasonView
                                        reason={record.complaintReason}
                                        currentLocale={normalizeLang(i18n.language)}
                                        showOriginalLabel={t('customerComplaint.detail.showOriginal')}
                                        showTranslationLabel={t('customerComplaint.detail.showTranslation')}
                                    />
                                </Descriptions.Item>
                            )}
                            <Descriptions.Item label={t('customerComplaint.table.trackingNumber')}>
                                {record.trackingNumber ?? '-'}
                            </Descriptions.Item>
                            {/* DSP 视角所有工单同属当前 DSP，展示自己的名字是冗余；仅 wms / HQ 展示 */}
                            {viewOnly && (
                                <Descriptions.Item label={t('customerComplaint.table.dsp')}>
                                    {record.dspName || record.dspId || '-'}
                                </Descriptions.Item>
                            )}
                            <Descriptions.Item label={t('customerComplaint.drawer.deliveryDriver')}>
                                {record.driverName || record.driverId || '-'}
                            </Descriptions.Item>
                            {(record.podImageS3UrlsJson?.length ?? 0) > 0 && (
                                <Descriptions.Item label={t('customerComplaint.drawer.pod')}>
                                    <Image.PreviewGroup>
                                        <div className="flex gap-3 flex-wrap" style={{rowGap: 12}}>
                                            {record.podImageS3UrlsJson!.map((url, index) => (
                                                <AttachmentTile key={index} url={url} />
                                            ))}
                                        </div>
                                    </Image.PreviewGroup>
                                </Descriptions.Item>
                            )}
                            {/*
                                收件人信息：供核验「是否投递到正确地址」——客诉正文里客户自述的地址
                                与订单上的收件人地址可能不一致，此处展示的是**订单上的**。
                            */}
                            <Descriptions.Item label={t('customerComplaint.drawer.recipientAddress')}>
                                {record.recipientAddress || '-'}
                            </Descriptions.Item>
                            <Descriptions.Item label={t('customerComplaint.drawer.recipientStandardizedAddress')}>
                                {record.recipientStandardizedAddress || '-'}
                            </Descriptions.Item>
                            <Descriptions.Item label={t('customerComplaint.drawer.recipientName')}>
                                {record.recipientName || '-'}
                            </Descriptions.Item>
                            <Descriptions.Item label={t('customerComplaint.drawer.recipientPhone')}>
                                {/* 区号与号码后端分开返回（与订单侧同构），展示侧组装 */}
                                {record.recipientPhoneNumber
                                    ? `${record.recipientPhoneCountryCode ? `+${record.recipientPhoneCountryCode} ` : ''}${record.recipientPhoneNumber}`
                                    : '-'}
                            </Descriptions.Item>
                            <Descriptions.Item label={t('customerComplaint.drawer.recipientCoordinates')}>
                                {record.recipientLatitude != null && record.recipientLongitude != null
                                    ? `${record.recipientLatitude}, ${record.recipientLongitude}`
                                    : '-'}
                            </Descriptions.Item>
                            <Descriptions.Item label={t('customerComplaint.drawer.orderOverallStatus')}>
                                {record.orderOverallStatus
                                    ? (t(`enum.overallStatus.${record.orderOverallStatus}`, {defaultValue: record.orderOverallStatus}) as string)
                                    : '-'}
                            </Descriptions.Item>
                            <Descriptions.Item label={t('customerComplaint.table.deadlineCountdown')}>
                                <span style={{color: '#ff4d4f'}}>
                                    {formatTime(record.appealDeadlineAt)}
                                    ，{t('customerComplaint.drawer.deadlineHint')}
                                </span>
                            </Descriptions.Item>
                        </Descriptions>
                    </Card>

                    {isPending && (
                        <Card
                            title={
                                <span style={{fontSize: 15, fontWeight: 600}}>
                                    {t('customerComplaint.drawer.appealInfo')}
                                </span>
                            }
                            bordered={false}
                            style={{borderRadius: 12, flex: 1}}
                        >
                            <div className="text-sm text-gray-500 mb-4">
                                {t('customerComplaint.drawer.appealInfoHint')}
                            </div>

                            {/* 图片凭证（最多 3 张） */}
                            <div className="mb-2" style={{fontSize: 15, fontWeight: 600}}>
                                {t('customerComplaint.drawer.imageAttachments')}
                            </div>
                            <Upload
                                listType="picture-card"
                                accept="image/*"
                                multiple
                                disabled={!canOperate}
                                beforeUpload={async (file) => {
                                    const result = await validateAppealFileWithFeedback(file);
                                    if (!result.ok) {
                                        message.warning(
                                            t(result.errorKey!, {...result.params}) as string,
                                        );
                                        return Upload.LIST_IGNORE;
                                    }
                                    if (imageFiles.length >= MAX_IMAGE_ATTACHMENTS) {
                                        // 批量勾选超过 3 张时多余的会静默丢弃，给一次性 warning 让用户感知
                                        message.warning(
                                            t('customerComplaint.drawer.maxImagesReached', {
                                                max: MAX_IMAGE_ATTACHMENTS,
                                            }) as string,
                                        );
                                        return Upload.LIST_IGNORE;
                                    }
                                    startUpload(file, setImageFiles); // 选时即预上传，提交时直接取 objectKey
                                    return false; // 阻止 antd 自动上传
                                }}
                                fileList={imageFiles.map<UploadFile>((item) => ({
                                    uid: item.uid,
                                    name: item.file.name,
                                    status: item.status,
                                    url: imagePreviewUrls.get(item.uid),
                                }))}
                                onPreview={(uploadFile) => {
                                    // 失败项点击即重传；成功项在新窗口预览原图
                                    const target = imageFiles.find((f) => f.uid === uploadFile.uid);
                                    if (target?.status === 'error') {
                                        retryUpload(target, setImageFiles);
                                    } else if (uploadFile.url) {
                                        window.open(uploadFile.url, '_blank');
                                    }
                                }}
                                onRemove={(uploadFile) => {
                                    setImageFiles((prev) => prev.filter((f) => f.uid !== uploadFile.uid));
                                }}
                                maxCount={MAX_IMAGE_ATTACHMENTS}
                            >
                                {/* 仅查看视角：保留"+上传"占位，置灰禁用（不隐藏，与备注一致：原样保留只做禁用） */}
                                {imageFiles.length < MAX_IMAGE_ATTACHMENTS && (
                                    <div style={canOperate ? undefined : {color: '#bfbfbf', cursor: 'not-allowed'}}>
                                        <PlusOutlined />
                                        <div style={{marginTop: 8}}>
                                            {t('customerComplaint.drawer.uploadFile')}
                                        </div>
                                    </div>
                                )}
                            </Upload>

                            {/* 其他凭证（录音 / 视频 / 文档） */}
                            <div className="mt-5 mb-1" style={{fontSize: 15, fontWeight: 600}}>
                                {t('customerComplaint.drawer.otherAttachments')}
                            </div>
                            <div className="text-xs text-gray-500 mb-2">
                                {t('customerComplaint.drawer.otherAttachmentsHint')}
                            </div>
                            <Upload
                                // 仅作为浏览器文件选择对话框的过滤提示；后端不强校验文件类型
                                accept="audio/*,video/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.zip,.rar"
                                multiple
                                disabled={!canOperate}
                                beforeUpload={async (file) => {
                                    const result = await validateAppealFileWithFeedback(file);
                                    if (!result.ok) {
                                        message.warning(
                                            t(result.errorKey!, {...result.params}) as string,
                                        );
                                        return Upload.LIST_IGNORE;
                                    }
                                    startUpload(file, setOtherFiles); // 选时即预上传
                                    return false;
                                }}
                                fileList={otherFiles.map<UploadFile>((item) => ({
                                    uid: item.uid,
                                    name: item.file.name,
                                    status: item.status,
                                }))}
                                onPreview={(uploadFile) => {
                                    // 失败项点击文件名即重传
                                    const target = otherFiles.find((f) => f.uid === uploadFile.uid);
                                    if (target?.status === 'error') retryUpload(target, setOtherFiles);
                                }}
                                onRemove={(uploadFile) => {
                                    setOtherFiles((prev) => prev.filter((f) => f.uid !== uploadFile.uid));
                                }}
                            >
                                {/* 仅查看视角：保留上传按钮，置灰禁用（不隐藏，与备注一致：原样保留只做禁用） */}
                                <Button icon={<UploadOutlined />} disabled={!canOperate}>
                                    {t('customerComplaint.drawer.uploadFile')}
                                </Button>
                            </Upload>

                            {/* 备注 */}
                            <div className="mt-5 mb-2" style={{fontSize: 15, fontWeight: 600}}>
                                <span style={{color: '#ff4d4f', marginRight: 4}}>*</span>
                                {t('customerComplaint.drawer.remark')}
                            </div>
                            <TextArea
                                rows={4}
                                value={appealReason}
                                onChange={(e) => setAppealReason(e.target.value)}
                                placeholder={
                                    canOperate
                                        ? (t('customerComplaint.drawer.remarkPlaceholder') as string)
                                        : ''
                                }
                                maxLength={APPEAL_REASON_MAX}
                                showCount={canOperate}
                                readOnly={!canOperate}
                                tabIndex={canOperate ? undefined : -1}
                                style={
                                    canOperate
                                        ? undefined
                                        : {resize: 'none', pointerEvents: 'none'}
                                }
                            />
                        </Card>
                    )}

                    {/* 非 PENDING 状态统一展示申诉详情卡；无申诉记录则字段全为 - */}
                    {!isPending && (
                        <Card
                            title={
                                <span style={{fontSize: 15, fontWeight: 600}}>
                                    {t('customerComplaint.drawer.appealDetail')}
                                </span>
                            }
                            bordered={false}
                            style={{borderRadius: 12, flex: 1}}
                        >
                            {appealLoading ? (
                                <Skeleton active />
                            ) : (
                                <Descriptions
                                    column={1}
                                    colon={false}
                                    labelStyle={{color: '#8c8c8c', width: 96}}
                                    contentStyle={{color: '#262626'}}
                                >
                                    <Descriptions.Item label={t('customerComplaint.drawer.appealReason')}>
                                        {pickDisplayText(appealDetail?.appealReason, normalizeLang(i18n.language)) || '-'}
                                    </Descriptions.Item>
                                    <Descriptions.Item label={t('customerComplaint.drawer.appealAttachments')}>
                                        {attachments.length > 0 ? (
                                            <Image.PreviewGroup>
                                                <div className="flex gap-3 flex-wrap" style={{rowGap: 12}}>
                                                    {attachments.map((url, index) => (
                                                        <AttachmentTile key={index} url={url} />
                                                    ))}
                                                </div>
                                            </Image.PreviewGroup>
                                        ) : '-'}
                                    </Descriptions.Item>
                                    <Descriptions.Item label={t('customerComplaint.drawer.appealSubmittedAt')}>
                                        {formatTime(appealDetail?.appealSubmittedAt) || '-'}
                                    </Descriptions.Item>
                                    <Descriptions.Item label={t('customerComplaint.drawer.appealSubmittedBy')}>
                                        {appealDetail?.appealSubmitterName || appealDetail?.appealSubmittedBy || '-'}
                                    </Descriptions.Item>
                                    <Descriptions.Item label={t('customerComplaint.drawer.reviewResult')}>
                                        {appealDetail?.reviewResult
                                            ? t(`customerComplaint.reviewResult.${appealDetail.reviewResult}`)
                                            : '-'}
                                    </Descriptions.Item>
                                    <Descriptions.Item label={t('customerComplaint.drawer.reviewedAt')}>
                                        {formatTime(appealDetail?.reviewedAt) || '-'}
                                    </Descriptions.Item>
                                    <Descriptions.Item label={t('customerComplaint.drawer.reviewComment')}>
                                        {renderReviewComment(appealDetail?.reviewComment, i18n.language)}
                                    </Descriptions.Item>
                                </Descriptions>
                            )}
                        </Card>
                    )}
                </Col>

                {/* 右侧：问题件记录时间轴 — 拉到底与左侧最末卡片底边对齐 */}
                <Col span={10} style={{display: 'flex'}}>
                    <Card
                        title={
                            <span style={{fontSize: 15, fontWeight: 600}}>
                                {t('customerComplaint.drawer.timeline')}
                            </span>
                        }
                        bordered={false}
                        style={{borderRadius: 12, flex: 1, width: '100%'}}
                    >
                        {appealLoading && !isPending ? (
                            <Skeleton active paragraph={{rows: 3}} />
                        ) : timelineItems.length > 0 ? (
                            <Timeline
                                items={timelineItems.map((item) => ({
                                    color: item.color,
                                    children: (
                                        <div>
                                            <div style={{fontSize: 14, fontWeight: 500, color: '#262626'}}>
                                                {item.title}
                                            </div>
                                            <div style={{fontSize: 12, color: '#8c8c8c', marginTop: 2}}>
                                                {item.label}
                                            </div>
                                            {item.description && (
                                                <div
                                                    style={{
                                                        fontSize: 12,
                                                        color: '#595959',
                                                        marginTop: 4,
                                                        wordBreak: 'break-all',
                                                    }}
                                                >
                                                    {item.description}
                                                </div>
                                            )}
                                        </div>
                                    ),
                                }))}
                            />
                        ) : (
                            <Text type="secondary">{t('customerComplaint.drawer.noTimeline')}</Text>
                        )}
                    </Card>
                </Col>
            </Row>
        </Drawer>
        <AbandonConfirmModal
            open={abandonAction !== null}
            loading={abandonLoading}
            onCancel={handleAbandonCancel}
            onConfirm={handleAbandonConfirm}
        />
        </>
    );
};

export default CustomerComplaintDetailDrawer;
